import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import {
  getUserProfile,
  updateProfile,
  addEducation,
  updateEducation,
  deleteEducation,
  addSkillGroup,
  updateSkillGroup,
  deleteSkillGroup,
  addExperience,
  updateExperience,
  deleteExperience,
  addProject,
  updateProject,
  deleteProject,
  addCertificate,
  updateCertificate,
  deleteCertificate,
  addExtraCurricular,
  updateExtraCurricular,
  deleteExtraCurricular,
  addCustomSection,
  updateCustomSection,
  deleteCustomSection
} from '../services/profile';

// Create Profile Context
const ProfileContext = createContext();

// Create Profile Provider component
export const ProfileProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    location: '',
    summary: '',
    education: [],
    skill_categories: [],
    experience: [],
    projects: [],
    certificates: [],
    extracurricular_activities: [],
    custom_sections: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch profile data when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchProfileData();
    }
  }, [isLoggedIn]);

  // Check profile completeness
  useEffect(() => {
    // Define minimum requirements for a complete profile
    const requiredBasicFields = ['first_name', 'last_name', 'email', 'phone_number', 'location'];
    const hasRequiredBasicInfo = requiredBasicFields.every(field => !!profileData??[field]);
    const hasEducation = profileData?.education?.length > 0;
    const hasSkills = profileData?.skill_categories?.length > 0;
    const hasExperience = profileData?.experience?.length > 0;
    
    // Consider profile complete if it has all required basic info and at least one education, skill, and experience
    setProfileComplete(hasRequiredBasicInfo && hasEducation && hasSkills && hasExperience);
  }, [profileData]);

  // Fetch user's profile data
  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getUserProfile();
      setProfileData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Failed to load profile data');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update entire profile
  const updateUserProfile = async (updatedData) => {
    setLoading(true);
    setError(null);
    
    try {
      await updateProfile(updatedData);
      setProfileData(updatedData);
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      console.error('Error updating profile:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Education CRUD operations
  const addEducationEntry = async (educationData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newEducation = await addEducation(educationData);
      setProfileData(prev => ({
        ...prev,
        education: [...prev.education, newEducation]
      }));
      setLastUpdated(new Date());
      return newEducation;
    } catch (err) {
      setError(err.message || 'Failed to add education');
      console.error('Error adding education:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateEducationEntry = async (id, educationData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEducation = await updateEducation(id, educationData);
      setProfileData(prev => ({
        ...prev,
        education: prev.education.map(edu => 
          edu.id === id ? { ...edu, ...updatedEducation } : edu
        )
      }));
      setLastUpdated(new Date());
      return updatedEducation;
    } catch (err) {
      setError(err.message || 'Failed to update education');
      console.error('Error updating education:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeEducationEntry = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteEducation(id);
      setProfileData(prev => ({
        ...prev,
        education: prev.education.filter(edu => edu.id !== id)
      }));
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete education');
      console.error('Error deleting education:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Skills CRUD operations
  const addSkill = async (skillData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newSkill = await addSkillGroup(skillData);
      setProfileData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill]
      }));
      setLastUpdated(new Date());
      return newSkill;
    } catch (err) {
      setError(err.message || 'Failed to add skill');
      console.error('Error adding skill:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSkill = async (id, skillData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedSkill = await updateSkillGroup(id, skillData);
      setProfileData(prev => ({
        ...prev,
        skills: prev.skills.map(skill => 
          skill.id === id ? { ...skill, ...updatedSkill } : skill
        )
      }));
      setLastUpdated(new Date());
      return updatedSkill;
    } catch (err) {
      setError(err.message || 'Failed to update skill');
      console.error('Error updating skill:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeSkill = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteSkillGroup(id);
      setProfileData(prev => ({
        ...prev,
        skills: prev.skills.filter(skill => skill.id !== id)
      }));
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete skill');
      console.error('Error deleting skill:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Experience CRUD operations
  const addExperienceEntry = async (experienceData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newExperience = await addExperience(experienceData);
      setProfileData(prev => ({
        ...prev,
        experience: [...prev.experience, newExperience]
      }));
      setLastUpdated(new Date());
      return newExperience;
    } catch (err) {
      setError(err.message || 'Failed to add experience');
      console.error('Error adding experience:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateExperienceEntry = async (id, experienceData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedExperience = await updateExperience(id, experienceData);
      setProfileData(prev => ({
        ...prev,
        experience: prev.experience.map(exp => 
          exp.id === id ? { ...exp, ...updatedExperience } : exp
        )
      }));
      setLastUpdated(new Date());
      return updatedExperience;
    } catch (err) {
      setError(err.message || 'Failed to update experience');
      console.error('Error updating experience:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeExperienceEntry = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteExperience(id);
      setProfileData(prev => ({
        ...prev,
        experience: prev.experience.filter(exp => exp.id !== id)
      }));
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete experience');
      console.error('Error deleting experience:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Projects CRUD operations
  const addProjectEntry = async (projectData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newProject = await addProject(projectData);
      setProfileData(prev => ({
        ...prev,
        projects: [...prev.projects, newProject]
      }));
      setLastUpdated(new Date());
      return newProject;
    } catch (err) {
      setError(err.message || 'Failed to add project');
      console.error('Error adding project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProjectEntry = async (id, projectData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedProject = await updateProject(id, projectData);
      setProfileData(prev => ({
        ...prev,
        projects: prev.projects.map(proj => 
          proj.id === id ? { ...proj, ...updatedProject } : proj
        )
      }));
      setLastUpdated(new Date());
      return updatedProject;
    } catch (err) {
      setError(err.message || 'Failed to update project');
      console.error('Error updating project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeProjectEntry = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteProject(id);
      setProfileData(prev => ({
        ...prev,
        projects: prev.projects.filter(proj => proj.id !== id)
      }));
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete project');
      console.error('Error deleting project:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Refresh profile data
  const refreshProfile = () => {
    if (isLoggedIn) {
      fetchProfileData();
    }
  };

  // Context value
  const value = {
    profileData,
    loading,
    error,
    profileComplete,
    lastUpdated,
    updateUserProfile,
    addEducationEntry,
    updateEducationEntry,
    removeEducationEntry,
    addSkill,
    updateSkill,
    removeSkill,
    addExperienceEntry,
    updateExperienceEntry,
    removeExperienceEntry,
    addProjectEntry,
    updateProjectEntry,
    removeProjectEntry,
    clearError,
    refreshProfile
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

// Custom hook to use the profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  
  return context;
};

export default ProfileContext;